import * as React from 'react';
import {
  type AuthCredentials,
  STORAGE_KEY,
  clearCredentials,
  readCredentials,
  writeCredentials,
} from './storage';
import type { UserProfile } from '@/api/types';
import { meApi } from '@/api/me';

interface AuthContextValue {
  credentials: AuthCredentials | null;
  user: UserProfile | null;
  loading: boolean;
  login: (creds: AuthCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [credentials, setCredentials] = React.useState<AuthCredentials | null>(() =>
    readCredentials(),
  );
  const [user, setUser] = React.useState<UserProfile | null>(null);
  const [loading, setLoading] = React.useState<boolean>(() => readCredentials() !== null);

  React.useEffect(() => {
    if (!credentials) {
      setUser(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    meApi
      .profile()
      .then((profile) => {
        if (!cancelled) {
          setUser(profile);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [credentials]);

  React.useEffect(() => {
    const handler = (): void => {
      clearCredentials();
      setCredentials(null);
      setUser(null);
    };
    window.addEventListener('zorro-ai:auth-cleared', handler);
    return () => window.removeEventListener('zorro-ai:auth-cleared', handler);
  }, []);

  React.useEffect(() => {
    const handler = (e: StorageEvent): void => {
      if (e.key !== null && e.key !== STORAGE_KEY) return;
      const next = readCredentials();
      setCredentials((prev) => {
        const prevKey = prev ? `${prev.email}:${prev.password}` : null;
        const nextKey = next ? `${next.email}:${next.password}` : null;
        return prevKey === nextKey ? prev : next;
      });
      if (!next) setUser(null);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      credentials,
      user,
      loading,
      login: async (creds) => {
        writeCredentials(creds);
        try {
          const profile = await meApi.profile();
          setCredentials(creds);
          setUser(profile);
        } catch (err) {
          clearCredentials();
          throw err;
        }
      },
      logout: () => {
        clearCredentials();
        setCredentials(null);
        setUser(null);
      },
    }),
    [credentials, user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
